import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { PlaylistLesson } from '@/store/playlist/types';
import { TemplateDarkMembersFooter } from './TemplateDarkMembersFooter';
import { Play, Clock, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import logoHostVslWhite from '@/assets/logo-hostvsl-white.png';
import logoHostVslIcon from '@/assets/hostvsl-icon.webp';

interface DarkMembersPlaylistViewProps {
  contentType: 'module' | 'bonus';
  contentId: string;
  title: string;
  lessons: PlaylistLesson[];
  thumbnailUrl?: string;
  description?: string;
  onBack: () => void;
  onLessonClick?: (lessonId: string) => void;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function DarkMembersPlaylistView({
  contentType,
  contentId,
  title,
  lessons,
  thumbnailUrl,
  description,
  onBack,
  onLessonClick,
}: DarkMembersPlaylistViewProps) {
  useLanguage();
  const [currentLessonId, setCurrentLessonId] = useState(
    lessons.length > 0 ? lessons[0].id : null
  );

  const sortedLessons = [...lessons].sort((a, b) => a.order - b.order);
  const currentLesson = sortedLessons.find(l => l.id === currentLessonId) || sortedLessons[0];

  const handleLessonSelect = (lessonId: string) => {
    setCurrentLessonId(lessonId);
    onLessonClick?.(lessonId);
  };

  return (
    <div data-theme-isolated className="min-h-screen flex flex-col" style={{ backgroundColor: '#0F0F0F' }}>
      {/* Header Dark com Logo + Botão Voltar Ghost + Breadcrumb */}
      <header 
        className="h-16 px-6 flex items-center sticky top-0 z-50"
        style={{ backgroundColor: '#0F0F0F', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Logo na Esquerda */}
        <a 
          href="https://www.hostvsl.com.br"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity"
        >
          <img 
            src={logoHostVslIcon} 
            alt={translateInterfaceText("HostVSL")}
            className="h-7 w-auto"
          />
        </a>

        {/* Botão Voltar Ghost */}
        <button
          onClick={onBack}
          className="ml-4 flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
          style={{
            backgroundColor: '#1A1A1A',
            color: '#FFFFFF',
            border: '1px solid #2A2A2A',
            borderRadius: '6px',
            padding: '6px 14px'
          }}
        >
          <ChevronLeft className="w-4 h-4" />
          {translateInterfaceText("Voltar")}</button>

        {/* Breadcrumb: Home > Módulo > Aula Atual */}
        <nav className="ml-4 flex items-center text-sm">
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>{translateInterfaceText("Home")}</span>
          <span className="mx-2" style={{ color: 'rgba(255,255,255,0.3)' }}>›</span>
          <span style={{ color: 'rgba(255,255,255,0.5)' }} className="hidden md:inline">{title}</span>
          <span className="mx-2 hidden md:inline" style={{ color: 'rgba(255,255,255,0.3)' }}>›</span>
          <span className="text-white font-medium truncate max-w-[200px] md:max-w-[400px]">
            {currentLesson?.title || translateInterfaceText("Selecione uma aula")}
          </span>
        </nav>
      </header>

      {/* Conteúdo Principal */}
      <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 lg:px-10 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Coluna Esquerda: Player + Título + Descrição */}
          <div className="flex-1 lg:max-w-[900px]">
            {/* Player */}
            <div 
              className="w-full bg-black overflow-hidden mb-6"
              style={{ aspectRatio: '16/9', borderRadius: '8px' }}
            >
              {currentLesson ? (
                <video
                  key={currentLesson.id}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  playsInline
                  src={`https://example.com/videos/${currentLesson.videoId}.mp4`}
                >
                  {translateInterfaceText("Seu navegador não suporta vídeos.")}</video>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  <Play className="w-20 h-20 text-white opacity-40" />
                </div>
              )}
            </div>

            {/* Título + Descrição sem estilo modal */}
            {currentLesson && (
              <div style={{ paddingTop: '16px', marginBottom: '16px' }}>
                <h1 style={{ 
                  fontSize: '20px', 
                  fontWeight: 600, 
                  color: '#FFFFFF',
                  marginBottom: '12px'
                }}>
                  {currentLesson.title}
                </h1>
                {currentLesson.description && (
                  <div 
                    className="prose prose-invert"
                    style={{ 
                      fontSize: '15px', 
                      fontWeight: 400,
                      color: '#CCCCCC',
                      maxWidth: '900px',
                      lineHeight: '1.6'
                    }}
                    dangerouslySetInnerHTML={{ __html: currentLesson.description }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Coluna Direita: Sidebar com Lista de Aulas */}
          <aside className="lg:w-[360px] flex-shrink-0">
            <div 
              className="overflow-hidden sticky top-24"
              style={{
                backgroundColor: '#121212',
                borderRadius: '12px',
                padding: '20px'
              }}
            >
              {/* Header da Sidebar */}
              <div 
                className="pb-4 mb-4"
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <h2 className="font-semibold text-white text-base mb-1">
                  {title}
                </h2>
                {description && (
                  <p className="text-sm line-clamp-2" style={{ color: '#9CA3AF' }}>
                    {description}
                  </p>
                )}
              </div>

              {/* Lista de Aulas */}
              <div 
                className="max-h-[500px] overflow-y-scroll scrollbar-dark -mx-5"
                style={{ scrollbarGutter: 'stable' }}
              >
                {sortedLessons.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-sm" style={{ color: '#9CA3AF' }}>
                      {translateInterfaceText("Nenhuma aula disponível")}</p>
                  </div>
                ) : (
                  <div>
                    {sortedLessons.map((lesson, index) => {
                      const isActive = lesson.id === currentLessonId;
                      
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => handleLessonSelect(lesson.id)}
                          className="w-full px-5 py-4 text-left transition-all duration-200"
                          style={{
                            backgroundColor: isActive ? '#FFFFFF' : 'transparent',
                            borderBottom: index < sortedLessons.length - 1 
                              ? '1px solid rgba(255,255,255,0.05)' 
                              : 'none',
                            borderLeft: isActive ? '3px solid #3A7AFE' : '3px solid transparent'
                          }}
                        >
                          <div className="flex items-start gap-3">
                            {/* Thumbnail com opacity condicional */}
                            <div 
                              className="w-24 h-14 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                              style={{
                                backgroundColor: '#0B1423',
                                backgroundImage: lesson.thumbnailUrl ? `url(${lesson.thumbnailUrl})` : undefined,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                opacity: isActive ? 1 : 0.7
                              }}
                            >
                              {!lesson.thumbnailUrl && (
                                <img src={logoHostVslWhite} alt="" className="w-8 opacity-40" />
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <h4 
                                className="text-sm font-medium line-clamp-2 mb-1"
                                style={{ color: isActive ? '#000000' : '#B5B5B5' }}
                              >
                                {lesson.title}
                              </h4>
                              <div 
                                className="flex items-center gap-1 text-xs"
                                style={{ color: isActive ? '#555555' : '#777777' }}
                              >
                                <Clock className="w-3 h-3" />
                                {formatDuration(lesson.duration)}
                              </div>
                            </div>

                            {/* Play Icon - só quando ativo */}
                            {isActive && (
                              <Play 
                                className="w-5 h-5 flex-shrink-0 fill-current" 
                                style={{ color: '#3A7AFE' }} 
                              />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Footer Dark Premium */}
      <TemplateDarkMembersFooter 
        showTerms={true}
        showPrivacy={true}
      />
    </div>
  );
}
